<template>
  <section class="bg-t3-bg-dark py-16 sm:py-24 px-4 sm:px-6 lg:px-20 max-w-7xl mx-auto font-t3-display" id="menu" aria-labelledby="menu-heading-t3">
    <div class="text-center mb-12 sm:mb-16">
      <span class="text-t3-primary font-bold tracking-widest uppercase text-xs">{{ $t('public.t3ExquisiteSelections') }}</span>
      <h2 id="menu-heading-t3" class="text-3xl sm:text-4xl font-bold mt-2 text-slate-100">{{ sectionTitle || $t('public.t3SeasonalMenu') }}</h2>
      <div class="w-20 h-1 bg-t3-primary mx-auto mt-4" aria-hidden="true" />
    </div>
    <p v-if="!displayGroups.length" class="text-slate-400 text-center py-12">{{ emptyMessage || $t('public.noMenuItems') }}</p>
    <template v-else>
      <div class="flex justify-center gap-4 sm:gap-8 mb-10 sm:mb-12 border-b border-white/10 overflow-x-auto">
        <button
          v-for="(group, gIdx) in displayGroups"
          :key="gIdx"
          type="button"
          class="pb-4 border-b-2 shrink-0 font-bold text-sm sm:text-base min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary rounded-t transition-colors"
          :class="activeGroupIndex === gIdx ? 'border-t3-primary text-t3-primary' : 'border-transparent text-slate-400 hover:text-slate-200'"
          @click="onGroupTabClick(group, gIdx)"
        >
          {{ group.category_name }}
        </button>
      </div>
      <p
        v-if="activeGroup?.category_description"
        class="text-slate-400 text-center text-sm max-w-2xl mx-auto mb-8 leading-relaxed"
      >
        {{ activeGroup.category_description }}
      </p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-16 gap-y-12 lg:gap-y-14">
        <template v-if="activeGroup && activeGroup.items.length">
        <article
          v-for="item in activeGroup.items"
          :key="item.uuid"
          class="group relative"
          :class="{ 'rms-item-unavailable': !isItemClickable(item, activeGroup) }"
        >
          <PublicMenuItemOwnerEditFab
            v-if="showOwnerEditFab"
            tone="dark"
            :item-uuid="item.catalog_item_uuid || item.uuid"
            :visible="showOwnerEditFab"
          />
          <template v-if="item.type === 'with_variants'">
            <button
              type="button"
              data-testid="public-menu-item"
              class="rms-t3-menu-item rms-t3-menu-item--variant-header w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary focus-visible:ring-offset-2 focus-visible:ring-offset-t3-bg-dark rounded-lg min-h-[44px] py-2"
              :aria-disabled="!isItemClickable(item, activeGroup)"
              @click="isItemClickable(item, activeGroup) ? onVariantHeaderClick(item, activeGroup.category_name, $event) : showUnavailableToast(activeGroup, item, now)"
            >
              <div class="flex w-full items-start gap-4">
                <img
                  v-if="itemCardDisplayImageUrl(item)"
                  :src="itemCardDisplayImageUrl(item)"
                  :alt="item.name || 'Untitled'"
                  loading="lazy"
                  class="w-20 h-20 sm:w-24 sm:h-24 shrink-0 aspect-square object-cover rounded-lg border border-white/10"
                />
                <div class="flex-1 min-w-0">
                  <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-1">
                    <h3 class="text-lg sm:text-xl font-semibold group-hover:text-t3-primary transition-colors text-slate-100 min-w-0 flex-1" :class="item.is_available === false ? 'text-slate-500' : ''">
                      {{ item.name || 'Untitled' }}
                    </h3>
                    <span
                      v-if="item.is_available !== false && isVariantSkuPubliclyOrderable(resolvedVariantSku(item)) && resolvedVariantSku(item)?.price != null"
                      class="text-t3-primary font-bold shrink-0 text-lg sm:text-xl"
                    >
                      {{ formatPrice(resolvedVariantSku(item).price) }}
                    </span>
                    <span v-else-if="item.is_available === false" class="text-slate-500 text-sm shrink-0">{{ $t('public.notAvailable') }}</span>
                  </div>
                  <p v-if="item.description" class="text-slate-400 text-sm leading-relaxed mt-0.5">{{ item.description }}</p>
                  <span v-if="itemTags(item).length" class="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <span
                      v-for="tag in itemTags(item)"
                      :key="tag.uuid"
                      class="rms-public-tag inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium min-h-[28px] transition-[opacity,background-color] duration-200"
                      :style="tagStyle(tag)"
                      :title="tag.text"
                      role="img"
                      :aria-label="tag.text"
                      tabindex="0"
                    >
                      <span class="material-symbols-outlined text-sm shrink-0" style="color: var(--tag-fg)" aria-hidden="true">{{ tag.icon || 'label' }}</span>
                      <span v-if="tag.text" class="whitespace-nowrap" style="color: var(--tag-fg)">{{ tag.text }}</span>
                    </span>
                  </span>
                </div>
              </div>
            </button>
            <div
              v-if="variantSkus(item).length && variantOptionGroups(item).length"
              class="mt-2 space-y-2 w-full pl-0 sm:pl-[5.5rem] md:pl-[7.5rem]"
              @click.stop
            >
              <div
                v-for="optGroup in variantOptionGroups(item)"
                :key="`${item.uuid}-${optGroup.name}`"
                class="space-y-1"
              >
                <label
                  class="block text-xs font-semibold text-slate-400"
                  :for="variantSelectId('rms-t3', item, optGroup.name)"
                >
                  {{ optGroup.name || $t('public.option') }}
                </label>
                <select
                  :id="variantSelectId('rms-t3', item, optGroup.name)"
                  class="rms-t3-variant-select w-full min-h-[44px] rounded-lg border border-white/15 bg-t3-bg-dark/80 text-slate-100 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary focus-visible:ring-offset-2 focus-visible:ring-offset-t3-bg-dark"
                  :value="variantSelectionValue(item, optGroup.name)"
                  :disabled="!isItemClickable(item, activeGroup)"
                  :aria-label="`${optGroup.name || $t('public.option')}: ${item.name || ''}`"
                  @change="onVariantGroupChange(item, optGroup.name, $event.target.value)"
                >
                  <option
                    v-for="val in (optGroup.values || [])"
                    :key="String(val)"
                    :value="val"
                    :disabled="!isVariantGroupValueOrderable(item, optGroup.name, val)"
                  >
                    {{ val }}{{ !isVariantGroupValueOrderable(item, optGroup.name, val) ? ' (' + $t('public.variantOptionNotAvailable') + ')' : '' }}
                  </option>
                </select>
              </div>
            </div>
            <p v-else-if="variantSkus(item).length && !variantOptionGroups(item).length" class="text-slate-500 text-xs sm:text-sm mt-1.5 pl-0 sm:pl-[5.5rem]">
              {{ variantOptionGroupsSummary(item) }}
            </p>
          </template>
          <button
            v-else
            type="button"
            data-testid="public-menu-item"
            class="rms-t3-menu-item w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary focus-visible:ring-offset-2 focus-visible:ring-offset-t3-bg-dark rounded-lg min-h-[44px] py-2"
            :aria-disabled="!isItemClickable(item, activeGroup)"
            @click="isItemClickable(item, activeGroup) ? onItemClick(item, activeGroup.category_name, $event) : showUnavailableToast(activeGroup, item, now)"
          >
            <div class="flex w-full items-start gap-4">
              <img
                v-if="itemCardDisplayImageUrl(item)"
                :src="itemCardDisplayImageUrl(item)"
                :alt="item.name || 'Untitled'"
                loading="lazy"
                class="w-20 h-20 sm:w-24 sm:h-24 shrink-0 aspect-square object-cover rounded-lg border border-white/10"
              />
              <div class="flex-1 min-w-0">
                <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-1">
                  <h3 class="text-lg sm:text-xl font-semibold group-hover:text-t3-primary transition-colors text-slate-100 min-w-0 flex-1" :class="item.is_available === false ? 'text-slate-500' : ''">
                    {{ item.name || 'Untitled' }}
                  </h3>
                  <span
                    v-if="item.is_available !== false && itemPrice(item) != null"
                    class="text-t3-primary font-bold shrink-0 text-lg sm:text-xl"
                  >
                    {{ formatPrice(itemPrice(item)) }}
                  </span>
                  <span v-else-if="item.is_available === false" class="text-slate-500 text-sm shrink-0">{{ $t('public.notAvailable') }}</span>
                </div>
                <p v-if="item.description" class="text-slate-400 text-sm leading-relaxed mt-0.5">{{ item.description }}</p>
                <span v-if="itemTags(item).length" class="flex flex-wrap items-center gap-1.5 mt-1.5">
                  <span
                    v-for="tag in itemTags(item)"
                    :key="tag.uuid"
                    class="rms-public-tag inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium min-h-[28px] transition-[opacity,background-color] duration-200"
                    :style="tagStyle(tag)"
                    :title="tag.text"
                    role="img"
                    :aria-label="tag.text"
                    tabindex="0"
                  >
                    <span class="material-symbols-outlined text-sm shrink-0" style="color: var(--tag-fg)" aria-hidden="true">{{ tag.icon || 'label' }}</span>
                    <span v-if="tag.text" class="whitespace-nowrap" style="color: var(--tag-fg)">{{ tag.text }}</span>
                  </span>
                </span>
                <ul v-if="item.type === 'combo' && comboEntries(item).length" class="mt-2 list-none pl-4 border-l-2 border-white/10 space-y-0.5 text-slate-500 text-xs sm:text-sm" aria-label="Combo contents">
                  <li v-for="(entry, eIdx) in comboEntries(item)" :key="eIdx">
                    {{ entry.name }} × {{ entry.quantity }}{{ entry.modifier_label ? ` (${entry.modifier_label})` : '' }}
                  </li>
                </ul>
              </div>
            </div>
          </button>
        </article>
      </template>
        <p v-else class="col-span-full text-slate-400 text-center py-8">{{ emptyMessage || $t('public.noMenuItems') }}</p>
      </div>
    </template>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { usePublicUnavailableToast } from '@/composables/usePublicUnavailableToast'
import { usePublicMenuDisplay } from '@/composables/usePublicMenuDisplay'
import { usePublicMenuVariantSelect } from '@/composables/usePublicMenuVariantSelect'
import { isAvailableNow } from '@/utils/availability'
import { isVariantSkuPubliclyOrderable } from '@/utils/publicVariantSku'

const emit = defineEmits(['select-item'])
const { showUnavailableToast, showUnavailableCategoryToast } = usePublicUnavailableToast()

const props = defineProps({
  menuGroups: { type: Array, default: () => [] },
  menuItems: { type: Array, default: () => [] },
  currency: { type: String, default: 'USD' },
  showCurrency: { type: Boolean, default: true },
  sectionTitle: { type: String, default: '' },
  /** Empty-state message (e.g. "No menu items", "No products", "No services") from parent based on business_type. */
  emptyMessage: { type: String, default: '' },
  showOwnerEditFab: { type: Boolean, default: false },
})

const {
  displayGroups,
  formatPrice,
  itemPrice,
  itemTags,
  comboEntries,
  variantOptionGroupsSummary,
  variantOptionGroups,
  variantSkus,
  itemCardImageUrl,
} = usePublicMenuDisplay(props)

const activeGroupIndex = ref(0)
const activeGroup = computed(() => displayGroups.value[activeGroupIndex.value] ?? null)
const now = ref(new Date())

const {
  variantSelectId,
  resolvedVariantSku,
  variantSelectionValue,
  onVariantGroupChange,
  isVariantGroupValueOrderable,
} = usePublicMenuVariantSelect({
  displayGroups,
  variantSkus,
  variantOptionGroups,
})

function tagStyle(tag) {
  const hex = tag?.color || '#64748b'
  return {
    '--tag-bg': `${hex}20`,
    '--tag-fg': hex,
  }
}

function onItemClick(item, categoryName, event) {
  emit('select-item', {
    item,
    categoryName: categoryName || 'Menu',
    sku: null,
    trigger: event?.currentTarget ?? null,
  })
}

function onVariantHeaderClick(item, categoryName, event) {
  const sku = resolvedVariantSku(item)
  emit('select-item', {
    item,
    categoryName: categoryName || 'Menu',
    sku: sku || null,
    trigger: event?.currentTarget ?? null,
  })
}

function categoryUnavailableNow(availability) {
  return availability != null && typeof availability === 'object' && !isAvailableNow(availability, now.value)
}

function itemUnavailableNow(availability) {
  return availability != null && typeof availability === 'object' && !isAvailableNow(availability, now.value)
}

function isItemClickable(item, group) {
  if (!item || !group) return false
  if (categoryUnavailableNow(group.availability)) return false
  if (item.is_available === false) return false
  if (itemUnavailableNow(item.availability)) return false
  if (item.type === 'with_variants') {
    const sku = resolvedVariantSku(item)
    if (!sku || !isVariantSkuPubliclyOrderable(sku)) return false
  }
  return true
}

function onGroupTabClick(group, gIdx) {
  activeGroupIndex.value = gIdx
  if (categoryUnavailableNow(group?.availability)) {
    showUnavailableCategoryToast(group, now)
  }
}

function itemCardDisplayImageUrl(item) {
  if (!item) return null
  if (item.type === 'with_variants') {
    const sku = resolvedVariantSku(item)
    return sku?.image_url ?? sku?.imageUrl ?? itemCardImageUrl(item)
  }
  return itemCardImageUrl(item)
}
</script>

<style scoped>
.rms-public-tag {
  background-color: var(--tag-bg);
}
.rms-public-tag:hover {
  opacity: 0.9;
}
.rms-t3-menu-item {
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  display: block;
}
article:has(.rms-t3-menu-item) {
  border: 1px solid white;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 0 10px 0 rgba(255, 255, 255, 0.1);
}
.rms-t3-menu-item:hover {
  opacity: 0.95;
}
.rms-t3-variant-select {
  appearance: auto;
}
.rms-item-unavailable {
  filter: grayscale(0.7) blur(0.5px);
  opacity: 0.7;
}
.rms-item-unavailable .rms-t3-menu-item {
  cursor: not-allowed;
}
</style>
